import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "../../../i18n";

const CameraModal: React.FC<{
  open: boolean;
  onClose: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  onStart: () => void;
  onCapture: () => void;
}> = ({ open, onClose, videoRef, onStart, onCapture }) => {
  const { t } = useTranslation();

  // -------------------------------------------
  // 🔹 Automatically start camera when modal opens
  // 🔹 Automatically stop camera when modal closes or unmounts
  // -------------------------------------------
  useEffect(() => {
    if (open) {
      onStart(); // start camera automatically
    } else if (videoRef.current && videoRef.current.srcObject) {
      // stop camera if modal is closed
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }

    // Cleanup when unmounted (safety net)
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, [open, onStart, videoRef]);

  // -------------------------------------------
  // 🔹 UI
  // -------------------------------------------
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t("camera_modal.title", "Camera")}
        {/* Close button → also stops camera via cleanup */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* 🎥 Live camera feed */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            borderRadius: 8,
            backgroundColor: "black",
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {/* Only capture button remains */}
        <Button variant="contained" onClick={onCapture}>
          {t("camera_modal.capture", "Capture")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraModal;
